// src/components/dashboard/AnalyticsPanel.jsx
// Inner content pane for Dashboard > Analytics (author view).
// The purple header bar and left nav from the design mockup are the app's
// existing global Navbar/Sidebar and are intentionally NOT rendered here.
import React, { useEffect, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { API_BASE } from '../../utils/api';

// --- palette from the design mockup ---
const C = {
  ink: '#1A1A1A',
  muted: '#6B6B6B',
  tile: '#F8F6FB',
  border: '#DDD5EA',
  grid: '#F1EFE8',
  accent: '#3C1F63',
};

const COMING_SOON = 'Coming soon';

// null/undefined -> "Coming soon"; otherwise formatted
const num = (v) => (v === null || v === undefined ? COMING_SOON : v.toLocaleString());
const pct = (v) => (v === null || v === undefined ? COMING_SOON : `${Math.round(v * 100)}%`);
const days = (v) => (v === null || v === undefined ? '—' : `${v} days`);

function StatTile({ label, value }) {
  const soon = value === COMING_SOON;
  return (
    <div style={{ background: C.tile, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 12, color: C.muted }}>{label}</div>
      <div
        style={{
          fontSize: soon ? 14 : 24,
          fontWeight: soon ? 500 : 700,
          color: soon ? C.muted : C.ink,
          marginTop: 6,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// Placeholder for charts deferred until the underlying time-series data exists.
function ChartPlaceholder({ title }) {
  return (
    <div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>{title}</div>
      <div
        style={{
          height: 220,
          borderRadius: 10,
          border: `1px dashed ${C.border}`,
          background: C.tile,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: C.muted,
          fontSize: 13,
        }}
      >
        {COMING_SOON}
      </div>
    </div>
  );
}

export default function AnalyticsPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const accessToken = localStorage.getItem('rv_access_token');
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/author/analytics`, {
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        setData(await res.json());
      } catch (e) {
        console.error(e);
        setError('Could not load analytics.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <CircularProgress />;
  if (error) return <Typography variant="body2" color="error">{error}</Typography>;

  const d = data || {};
  const royalty = d.royalty?.placeholder ? '$0' : `$${(d.royalty?.amount ?? 0).toLocaleString()}`;
  const reviewers = Array.isArray(d.reviewers) ? d.reviewers : [];

  return (
    <div>
      {/* Title */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.ink }}>Analytics</div>
      </div>

      {/* Stat tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
          gap: 14,
          marginBottom: 28,
        }}
      >
        <StatTile label="Words written" value={num(d.wordsWritten)} />
        <StatTile label="Active readers" value={num(d.activeReaders)} />
        <StatTile label="Royalty" value={royalty} />
        <StatTile label="Idea branches" value={num(d.ideaBranches)} />
      </div>

      {/* Charts (deferred) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.3fr 1fr',
          gap: 20,
          marginBottom: 28,
        }}
      >
        <ChartPlaceholder title="Words written per week" />
        <ChartPlaceholder title="Engagement by feature" />
      </div>

      {/* Market performance */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Market performance</div>
        <span
          style={{
            fontSize: 10,
            color: C.muted,
            background: C.grid,
            padding: '3px 10px',
            borderRadius: 20,
          }}
        >
          external data
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
          gap: 14,
          marginBottom: 28,
        }}
      >
        <MarketCard
          label="Bestseller rank"
          value={d.market?.bestsellerRank == null ? COMING_SOON : `#${d.market.bestsellerRank}`}
          sub="Amazon · Fantasy"
        />
        <MarketCard label="Completion rate" value={pct(d.completionRate)} sub="E-reader platforms" />
        <MarketCard
          label="Projects Started"
          value={num(d.projectsStarted)}
          sub={`Completed · ${d.projectsCompleted ?? 0} projects`}
        />
      </div>

      {/* Reviewer performance */}
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Reviewer performance</div>
      <div style={{ border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
            background: C.tile,
            padding: '10px 16px',
            fontSize: 11,
            color: C.muted,
          }}
        >
          <div>Reviewer</div>
          <div>Reviews</div>
          <div>Accept rate</div>
          <div>Avg. turnaround</div>
        </div>
        {reviewers.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: 13, color: C.muted, borderTop: `0.5px solid ${C.border}` }}>
            No reviewer activity yet.
          </div>
        ) : (
          reviewers.map((r, i) => (
            <div
              key={`${r.name}-${i}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                padding: '11px 16px',
                fontSize: 13,
                color: C.ink,
                borderTop: `0.5px solid ${C.border}`,
              }}
            >
              <div>{r.name}</div>
              <div>{r.reviews}</div>
              <div>{pct(r.acceptRate)}</div>
              <div>{days(r.avgTurnaroundDays)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MarketCard({ label, value, sub }) {
  const soon = value === COMING_SOON;
  return (
    <div style={{ background: C.tile, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 12, color: C.muted }}>{label}</div>
      <div
        style={{
          fontSize: soon ? 14 : 24,
          fontWeight: soon ? 500 : 700,
          color: soon ? C.muted : C.ink,
          marginTop: 6,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{sub}</div>
    </div>
  );
}
